/**
 * Lightbox voor een foto uit een ballon of mail (Brain grote-ronde/75 keuze 8):
 * niet schermvullend (hooguit 80 procent van het scherm), met een kruisje, en
 * Escape of een klik naast de foto sluit hem. De focus gaat naar het kruisje en
 * na sluiten terug naar wat hem opende.
 *
 * Via een portal in de werkplek-wortel (`.v6`, voor de kleurtokens): binnen een
 * vak met `overflow: hidden` (de klantmail) neemt iOS-WebKit buiten dat vak geen
 * tikken aan, ook niet op een vast gepositioneerd kruisje (Simulator, 14-10).
 */
import { useEffect, useRef, type TouchEvent } from "react";
import { createPortal } from "react-dom";

import { Icoon } from "../Icoon";

export function Lightbox({
  url,
  beschrijving,
  onSluit,
}: {
  url: string;
  beschrijving: string;
  onSluit: () => void;
}) {
  const dicht = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const vorige = document.activeElement as HTMLElement | null;
    // Zonder schuiven: anders verschuift iOS-Safari de pagina en landt de click
    // van een tik 40 px naast het kruisje (Simulator, 14-10).
    dicht.current?.focus({ preventScroll: true });
    const toets = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onSluit();
      }
    };
    document.addEventListener("keydown", toets);
    return () => {
      document.removeEventListener("keydown", toets);
      vorige?.focus?.();
    };
  }, [onSluit]);

  // iOS: sluiten bij het loslaten van de tik zelf. De click die WebKit daarna
  // afleidt kan na schuiven tientallen pixels naast de vinger landen (gemeten in
  // de Simulator, 14-10); preventDefault voorkomt ook dat die click doorschiet
  // naar de pagina onder de lightbox.
  const tikDicht = (e: TouchEvent) => {
    e.preventDefault();
    onSluit();
  };
  // Naast de foto: alleen een tik, geen veeg of knijpbeweging.
  const begin = useRef<{ x: number; y: number } | null>(null);
  const tikBegin = (e: TouchEvent) => {
    const t = e.touches[0];
    begin.current = t ? { x: t.clientX, y: t.clientY } : null;
  };
  const tikNaast = (e: TouchEvent) => {
    if (e.target !== e.currentTarget) return;
    const t = e.changedTouches[0];
    const b = begin.current;
    if (b && t && Math.hypot(t.clientX - b.x, t.clientY - b.y) > 10) return;
    tikDicht(e);
  };
  const doel =
    (typeof document !== "undefined" && document.querySelector(".v6")) ||
    document.body;
  return createPortal(
    <div
      className="m7-lightbox"
      data-testid="m7-lightbox"
      onClick={(e) => e.target === e.currentTarget && onSluit()}
      onTouchStart={tikBegin}
      onTouchEnd={tikNaast}
    >
      <figure
        className="doek"
        role="dialog"
        aria-modal="true"
        aria-label={beschrijving || "Foto"}
      >
        <div className="r">
          <span className="naam">{beschrijving}</span>
          <button
            ref={dicht}
            type="button"
            className="dicht"
            aria-label="Sluit foto"
            onClick={onSluit}
            onTouchEnd={tikDicht}
          >
            <Icoon naam="x" klein />
          </button>
        </div>
        <img src={url} alt={beschrijving} />
      </figure>
    </div>,
    doel,
  );
}
